import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { cn } from "components/lib";

const SCRIPT_ID = "cloudflare-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.turnstile), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const Turnstile = forwardRef(function Turnstile(
  { siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY, onVerify, onExpire, className },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState(null);

  useImperativeHandle(ref, () => ({
    reset() {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
        onVerify?.("");
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      setError("Turnstile site key is not configured.");
      return undefined;
    }

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || widgetIdRef.current) return;

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerify?.(token),
          "expired-callback": () => {
            onVerify?.("");
            onExpire?.();
          },
          "error-callback": () => {
            onVerify?.("");
            setError("Security check failed to load. Please try again.");
          },
        });
      })
      .catch(() => setError("Security check failed to load. Please try again."));

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onVerify, onExpire]);

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div ref={containerRef} />
      {error && <p className="text-xs text-red-300">{error}</p>}
    </div>
  );
});

export { Turnstile };
